import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { IoIosArrowDown } from 'react-icons/io'
import { v4 as uuidv4 } from 'uuid'
import { IconSearch } from '../../components/icons/IconSearch'
import FormHeader from '../../components/ui/form/form-header/FormHeader'
import { CONST } from '../../const/const'
import { HORSE_MEMBERSHIP_GROUPS } from '../../const/membership-const'
import { MESSAGES_CONST } from '../../const/messages-const'
import useToasterHelper from '../../helpers/ToasterHelper'
import { HorseMembershipGroupKey, IHorseData } from '../../models/horse/horse.interface'
import { HorseModel } from '../../models/horse/horse.model'
import { getConvertedData } from '../../models/interface.helper'
import FirestoreService from '../../services/firestoreService'
import { useAppDispatch } from '../../store/hooks'
import { setSelectedHorse } from '../../store/horses/horseSlice'
import { HorseNumberCard } from './components/HorseNumberCard'
import { EmptyCards } from '../../components/user/views/profile/OrganizerMembershipsTab/components/EmptyCards/EmptyCards'

interface HorseMembershipsTabProps {
  horseId: string | null
  savedMode?: boolean
}

export const HorseMembershipsTab: React.FC<HorseMembershipsTabProps> = ({
  horseId,
  savedMode = true,
}) => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const [loading, setLoading] = useState(false)
  const [horseData, setHorseData] = useState<IHorseData | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [saved, setSaved] = useState(savedMode)
  const [activeTab, setActiveTab] = useState<HorseMembershipGroupKey>('English')
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])

  const { reset, watch, control, register, setValue, handleSubmit } = useForm<IHorseData>({
    mode: 'onChange',
  })

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'horsesOther',
  })

  // Local var for horse membership const data
  const membershipGroups = HORSE_MEMBERSHIP_GROUPS

  useEffect(() => {
    const getHorseData = async () => {
      if (!horseId) return

      const horseSnapshot = await FirestoreService.getItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.HORSES.NAME,
        horseId
      )

      if (horseSnapshot.exists()) {
        const horse = HorseModel.fromFirestoreDoc(horseSnapshot).toObject()
        reset({ ...horse })
        setHorseData(horse)
      }
    }
    getHorseData()
  }, [horseId, reset])

  useEffect(() => {
    if (!searchValue || !horseData) {
      setSearchResults([])
      return
    }

    // all memberships from all tabs
    const allMemberships = Object.keys(membershipGroups).flatMap((tab) =>
      membershipGroups[tab as HorseMembershipGroupKey].map((membership) => ({
        ...membership,
        tab,
        type: 'standard' as const,
        number: (horseData[membership.numberKey as keyof IHorseData] as string) || '',
      }))
    )

    // add custom fields to the search results
    const customFields = fields.map((field, index) => ({
      numberKey: `horsesOther.${index}.number`,
      nameKey: `horsesOther.${index}.name`,
      expiresKey: `horsesOther.${index}.date`,
      imageKey: `horsesOther.${index}.image`,
      label: field.name || 'Custom Membership',
      number: field.number || '',
      tab: 'Other' as const,
      type: 'custom' as const,
      id: field.id,
    }))

    const combinedMemberships = [...allMemberships, ...customFields]

    const searchTermLower = searchValue.toLowerCase()
    // Filter based on search value
    const filteredResults = combinedMemberships.filter((membership) => {
      return (
        membership.label.toLowerCase().includes(searchTermLower) ||
        membership.number.toLowerCase().includes(searchTermLower)
      )
    })

    setSearchResults(filteredResults)
  }, [searchValue, membershipGroups, fields, horseData])

  const onSubmit = async (dataForm: any) => {
    setLoading(true)
    try {
      // Update the horse data in Firestore
      if (horseId) {
        await FirestoreService.updateItem(
          CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.HORSES.NAME,
          horseId,
          dataForm
        )

        // Update the horse data in Redux
        dispatch(setSelectedHorse(getConvertedData(dataForm)))

        setHorseData(dataForm)

        toastFunctions.success({ message: MESSAGES_CONST.HORSE_UPDATE_SUCCESSFULLY })
        setSaved(true)
      }
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // check if a membership num is completed (has both a number and expiration date)
  const isMembershipCompleted = useCallback(
    (numberKey: string, expiresKey?: string) => {
      if (!horseData) return false

      // for custom fields in the horsesOther array
      if (numberKey.startsWith('horsesOther.')) {
        const index = parseInt(numberKey.split('.')[1])
        // Check if this index exists in the fields array - this is used for "other" nums
        if (fields && fields[index]) {
          const field = fields[index]
          return (
            field &&
            field.number &&
            typeof field.number === 'string' &&
            field.number.trim() !== '' &&
            field.date &&
            (typeof field.date === 'string' ? field.date.trim() !== '' : true)
          )
        }
        return false
      }

      // for standard membership fields
      const numberValue = horseData[numberKey as keyof IHorseData] as string | undefined | Date
      const expiresValue = expiresKey
        ? (horseData[expiresKey as keyof IHorseData] as string | undefined | Date)
        : undefined

      return (
        !!numberValue &&
        (typeof numberValue === 'string' ? numberValue.trim() !== '' : true) &&
        !!expiresKey &&
        !!expiresValue &&
        (typeof expiresValue === 'string' ? expiresValue.trim() !== '' : true)
      )
    },
    [horseData, fields]
  )

  // filtered memberships for current tab - any tab that is not "other"
  const { completed: completedMemberships, incomplete: incompleteMemberships } = useMemo(() => {
    if (!activeTab) return { completed: [], incomplete: [] }

    const memberships = membershipGroups[activeTab] || []

    return {
      completed: memberships.filter((membership) =>
        isMembershipCompleted(membership.numberKey, membership.expiresKey)
      ),
      incomplete: memberships.filter(
        (membership) => !isMembershipCompleted(membership.numberKey, membership.expiresKey)
      ),
    }
  }, [activeTab, membershipGroups, isMembershipCompleted])

  // filtered custom fields (for "other" tab)
  const { completed: completedCustomFields, incomplete: incompleteCustomFields } = useMemo(() => {
    return {
      completed: fields.filter((_, index) =>
        isMembershipCompleted(`horsesOther.${index}.number`, `horsesOther.${index}.date`)
      ),
      incomplete: fields.filter(
        (_, index) =>
          !isMembershipCompleted(`horsesOther.${index}.number`, `horsesOther.${index}.date`)
      ),
    }
  }, [fields, isMembershipCompleted])

  const renderHeaderButtons = () => {
    return (
      <div className="flex flex-col sm:flex-row w-full items-center sm:justify-end gap-2 sticky top-0">
        <div className="border relative h-[46px] rounded-lg w-full md:max-w-[449px] border-SeabiscuitSkeletonColor">
          <div className="opacity-50 absolute inset-y-0 left-0 flex z-50 items-center pl-3 pointer-events-none">
            <IconSearch />
          </div>
          <input
            type="search"
            className="rounded-lg block w-full bg-transparent p-0 pl-10 text-sm border-0 h-full text-gray-900 placeholder:text-SeabiscuitDark200ThemeColor"
            placeholder="Search memberships..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
        {saved ? (
          <button
            type="button"
            onClick={() => setSaved(false)}
            className="items-center !sticky top-0 bg-white right-10 w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
          >
            Edit
          </button>
        ) : (
          <div className="flex-col flex sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => {
                reset({ ...horseData }) // Reset form to original data
                setSaved(true) // Exit edit mode
              }}
              className="items-center w-[150px] h-[45px] border rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitLightTextColor bg-SeabiscuitLightThemeColor hover:text-white hover:bg-SeabiscuitMainThemeColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-SeabiscuitMainThemeColor"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit(onSubmit)}
              className="items-center w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
            >
              {loading ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <FormHeader
        title="Horse Memberships"
        removeBreakLine
        description="Review and manage your horse's memberships"
        headerButtonsContainer={renderHeaderButtons()}
      />
      {/* discipline tabs */}
      <div className="flex relative mb-6">
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gray-200"></div>
        {/* mobile menu - dropdown */}
        <div className="md:hidden w-full">
          <div className="relative">
            <button
              className="flex items-center justify-between w-full py-2 px-4 font-medium text-sm focus:outline-none text-SeabiscuitMainThemeColor border-b"
              onClick={() => setIsOpen(!isOpen)}
            >
              <span>{activeTab}</span>
              <IoIosArrowDown
                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="absolute z-10 w-full bg-white border shadow-lg rounded-b-md">
                {Object.keys(membershipGroups).map((discipline) => (
                  <button
                    key={discipline}
                    className={`w-full text-left py-3 px-4 text-sm ${
                      activeTab === discipline
                        ? 'bg-gray-50 text-SeabiscuitMainThemeColor'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setActiveTab(discipline as HorseMembershipGroupKey)
                      setIsOpen(false)
                    }}
                  >
                    {discipline}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* horizontal tabs */}
        <div className="hidden md:flex">
          {Object.keys(membershipGroups).map((discipline) => (
            <button
              key={discipline}
              className={`py-2 px-6 font-medium text-nr whitespace-nowrap focus:outline-none relative ${
                activeTab === discipline
                  ? 'text-SeabiscuitMainThemeColor'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(discipline as HorseMembershipGroupKey)}
            >
              {discipline}
              {activeTab === discipline && (
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-SeabiscuitMainThemeColor"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* render search results or filtered memberships based on if there is a searchValue */}
      {searchValue.trim() ? (
        // render search results
        <div className="flex flex-col">
          {searchResults.length > 0 ? (
            <div className="membership-grid-container">
              {searchResults.map((membership) =>
                membership.type === 'standard' ? (
                  <HorseNumberCard
                    key={membership.numberKey}
                    icon={membership.icon}
                    label={membership.label}
                    numberKey={membership.numberKey}
                    expiresKey={membership.expiresKey}
                    saved={saved}
                    register={register}
                    control={control}
                    horseData={horseData}
                    setHorseData={setHorseData}
                    setValue={setValue}
                    update={update}
                    watch={watch}
                  />
                ) : (
                  <HorseNumberCard
                    key={membership.id}
                    numberKey={membership.numberKey}
                    nameKey={membership.nameKey}
                    expiresKey={membership.expiresKey}
                    imageKey={membership.imageKey}
                    saved={saved}
                    register={register}
                    control={control}
                    horseData={horseData}
                    setHorseData={setHorseData}
                    setValue={setValue}
                    update={update}
                    watch={watch}
                    onRemove={
                      !saved
                        ? () => {
                            const index = parseInt(membership.numberKey.split('.')[1])
                            remove(index)
                          }
                        : undefined
                    }
                  />
                )
              )}
              <EmptyCards length={searchResults.length} />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              No memberships found matching your search
            </div>
          )}
        </div>
      ) : (
        //  rendering based on active tab (when no search values)
        <div className="flex flex-col">
          {activeTab === 'Other' ? (
            // other tab rendering with custom fields
            <>
              {/* completed custom fields */}
              {completedCustomFields.length > 0 && (
                <div className="membership-grid-container">
                  {completedCustomFields.map((field) => {
                    const index = fields.findIndex((f) => f.id === field.id)
                    return (
                      <HorseNumberCard
                        key={field.id}
                        numberKey={`horsesOther.${index}.number`}
                        nameKey={`horsesOther.${index}.name`}
                        expiresKey={`horsesOther.${index}.date`}
                        imageKey={`horsesOther.${index}.image`}
                        saved={saved}
                        register={register}
                        control={control}
                        horseData={horseData}
                        setHorseData={setHorseData}
                        setValue={setValue}
                        update={update}
                        watch={watch}
                        onRemove={!saved ? () => remove(index) : undefined}
                      />
                    )
                  })}
                  <EmptyCards length={completedCustomFields.length} />
                </div>
              )}

              {/* divider between completed and incomplete custom fields */}
              {completedCustomFields.length > 0 && incompleteCustomFields.length > 0 && (
                <div className="my-6 w-full h-[1px] bg-gray-100"></div>
              )}

              {/* incomplete custom fields */}
              <div className="membership-grid-container">
                {incompleteCustomFields.map((field) => {
                  const index = fields.findIndex((f) => f.id === field.id)
                  return (
                    <HorseNumberCard
                      key={field.id}
                      numberKey={`horsesOther.${index}.number`}
                      nameKey={`horsesOther.${index}.name`}
                      expiresKey={`horsesOther.${index}.date`}
                      imageKey={`horsesOther.${index}.image`}
                      saved={saved}
                      register={register}
                      control={control}
                      horseData={horseData}
                      setHorseData={setHorseData}
                      setValue={setValue}
                      update={update}
                      watch={watch}
                      onRemove={!saved ? () => remove(index) : undefined}
                    />
                  )
                })}
                <EmptyCards length={incompleteCustomFields.length} />
              </div>

              {/* Add number button */}
              <div className="w-auto inline-block">
                <button
                  type="button"
                  className="flex border items-center justify-start cursor-pointer hover:opacity-70 w-[120px] h-[40px] self-center mt-4 transition-all"
                  onClick={() => {
                    // if in view mode, switch to edit mode
                    if (saved) {
                      setSaved(false)
                    }

                    // always append a new item
                    append({
                      name: '',
                      number: '',
                      date: '',
                      uuid: uuidv4(),
                      image: '',
                    })
                  }}
                >
                  <img src="/assets/cp_icons/Cancel-2.svg" className="w-4 rotate-45" alt="" />
                  <p className="ml-2 text-sm text-SeabiscuitMainThemeColor">Add number</p>
                </button>
              </div>
            </>
          ) : (
            // standard tabs rendering
            <>
              {/* completed standard memberships */}
              {completedMemberships.length > 0 && (
                <div className="membership-grid-container">
                  {completedMemberships.map((membership) => (
                    <HorseNumberCard
                      key={membership.numberKey}
                      icon={membership.icon}
                      label={membership.label}
                      numberKey={membership.numberKey}
                      expiresKey={membership.expiresKey}
                      saved={saved}
                      register={register}
                      control={control}
                      horseData={horseData}
                      setHorseData={setHorseData}
                      setValue={setValue}
                      update={update}
                      watch={watch}
                    />
                  ))}
                  <EmptyCards length={completedMemberships.length} />
                </div>
              )}

              {/* divider between completed and incomplete standard memberships */}
              {completedMemberships.length > 0 && incompleteMemberships.length > 0 && (
                <div className="my-6 w-full bg-gray-100 h-[1px]"></div>
              )}

              {/* incomplete standard memberships */}
              <div className="membership-grid-container">
                {incompleteMemberships.map((membership) => (
                  <HorseNumberCard
                    key={membership.numberKey}
                    icon={membership.icon}
                    label={membership.label}
                    numberKey={membership.numberKey}
                    expiresKey={membership.expiresKey}
                    saved={saved}
                    register={register}
                    control={control}
                    horseData={horseData}
                    setHorseData={setHorseData}
                    setValue={setValue}
                    update={update}
                    watch={watch}
                  />
                ))}
                <EmptyCards length={incompleteMemberships.length} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default HorseMembershipsTab
